import { useState } from 'react';
import { showAlert } from '../../../../../main_components/alerts/alert_response';

// Tipos
interface AttachedFile {
  file: File;
  renamedName: string;
}

interface UseAttachedFilesProps {
  notificationSystemRef: React.MutableRefObject<any>;
}

const allowedExtensions = {
  documents: ['pdf', 'txt', 'csv', 'tsv', 'zip', 'rar', 'doc', 'docx', 'xls', 'xlsx', 'odt', 'ods', 'xml'],
  images: ['jpg', 'jpeg', 'png'],
  audio: ['mp3', 'wav', 'ogg', 'oga', 'flac', 'aac', 'm4a', 'wma', 'amr', 'opus'],
};

export const useAttachments = ({ notificationSystemRef }: UseAttachedFilesProps) => {
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);

  const formatFileNameByType = (file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const now = new Date();
    const timestamp = `${now.getFullYear()}${(now.getMonth() + 1)
      .toString()
      .padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getTime()}`;

    if (allowedExtensions.documents.includes(ext)) return `DOC__${timestamp}.${ext}`;
    if (allowedExtensions.images.includes(ext)) return `IMG__${timestamp}.${ext}`;
    if (allowedExtensions.audio.includes(ext)) return `AUDIO__${timestamp}.${ext}`;
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const newFiles = Array.from(e.target.files);
    const validFiles: AttachedFile[] = [];

    newFiles.forEach((file) => {
      const maxSizeMB = 4.9;
      const fileSizeMB = file.size / (1024 * 1024);

      if (fileSizeMB > maxSizeMB) {
        showAlert({
          msj: `El archivo "${file.name}" supera el tamaño máximo de ${maxSizeMB} MB.`,
          status: false,
          notificationSystemRef,
        });
        return;
      }

      const renamed = formatFileNameByType(file);
      if (!renamed) {
        showAlert({
          msj: `Formato no permitido: ${file.name}`,
          status: false,
          notificationSystemRef,
        });
        return;
      }

      validFiles.push({ file, renamedName: renamed });
    });

    setAttachedFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return {
    attachedFiles,
    setAttachedFiles,
    handleFileChange,
    removeFile,
    formatFileNameByType,
  };
};
